import {ChangeEvent, ReactElement, useState, FormEvent, useEffect} from "react";
import {Alert, Avatar, Button, CssBaseline, TextField, Box, Typography, Container, CircularProgress, FormControl, InputLabel, Select, MenuItem, SelectChangeEvent} from '@mui/material';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import md5 from "md5";


export default function RegistrationForm(): ReactElement {
    const [anonId, setAnonId] = useState(0);
    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [organisation, setOrganisation] = useState("");
    const [streetAddress, setStreetAddress] = useState("");
    const [city, setCity] = useState("");
    const [state, setState] = useState("");
    const [postalCode, setPostalCode] = useState("");
    const [region, setRegion] = useState("");
    const [email, setEmail] = useState("");
    const [showError, setShowError] = useState<boolean>(false);
    const [showSuccess, setShowSuccess] = useState<boolean>(false);
    const [showButton, setShowButton] = useState(false);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        setAnonId(Date.now());
    }, []);
    
    const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setShowSuccess(false);
        setShowError(false);
        setLoading(true);
        fetch("https://advances.vsb.cz" + "/eps/v1/contact", {
            method: 'POST',
            headers: {
                "Content-Type": "application/json"
            },
           body: JSON.stringify({
               anon_id: anonId,
               name: firstName,
               last_name: lastName,
               organisation: organisation,
               address: streetAddress,
               city: city,
               state: state,
               postal_code: postalCode,
               region: region,
               email: email,
           })
        }).then((response) => {
            if(Boolean(response.ok)) {
                return response.json();
            }
            throw new Error("The specified user cannot be registered.")
        })
            .then(() => {
                setShowError(false);
                setShowSuccess(true);
                setLoading(false);
                setTimeout(() => {
                    setShowButton(true)
                }, 2000);
            })
            .catch((error) => {
                setShowError(true);
                setShowSuccess(false);
                setLoading(false);
            });
    };
    
    const handlePayment = () => {
        const token = md5(anonId + (process.env["REACT_APP_ANON_PASS"] ?? "") + process.env["REACT_APP_ANON_SYS"]);

        window.location.replace('https://eps.vsb.cz?anon_sys=' + process.env["REACT_APP_ANON_SYS"] + 
            '&anon_id=' + anonId + '&token=' + token + '&url=https%3A%2F%2Fadvances.vsb.cz%2Fpayments%2Fpayment-info?anonId=' + anonId + '&eps_lang=English&anon_fast=1');
    }
    
    const isSubmitEnabled = (): boolean => {
        return firstName.length > 0 && lastName.length > 0 && streetAddress.length > 0 && city.length > 0 && state.length > 0 && postalCode.length > 0 && email.length > 0 && region.length > 0;
    }

    return (
            <Container component="main" maxWidth="xs">
                <CssBaseline />
                <Box
                    sx={{
                        marginTop: 8,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                    }}
                >
                    <Avatar sx={{ m: 1, bgcolor: 'primary.main' }}>
                        <PersonAddIcon />
                    </Avatar>
                    <Typography component="h1" variant="h5">
                        Billing Information
                    </Typography>
                    {showButton 
                        ? <Box  sx={{ mt: 1 }}> <Button fullWidth variant="contained" sx={{ mt: 3, mb: 2 }} onClick={handlePayment}>Proceed to payment</Button> </Box>
                        : <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 1 }}>
                            {showError && <Alert severity="error"> Error while saving the billing information. Please, contact the administrator. </Alert>}
                            {showSuccess && <Alert severity="success"> Billing information has been successfully saved. </Alert>}
                            <TextField
                                autoFocus
                                margin="dense"
                                id="billing-contact"
                                label="First name"
                                type="text"
                                fullWidth
                                value={firstName}
                                onChange={(event: ChangeEvent<HTMLInputElement>) => setFirstName(event.target.value)}
                            />
                            <TextField
                                margin="dense"
                                id="billing-contact"
                                label="Last name"
                                type="text"
                                fullWidth
                                value={lastName}
                                onChange={(event: ChangeEvent<HTMLInputElement>) => setLastName(event.target.value)}
                            />
                            <TextField
                                margin="dense"
                                id="billing-contact"
                                label="Organisation (optional)"
                                type="text"
                                fullWidth
                                value={organisation}
                                onChange={(event: ChangeEvent<HTMLInputElement>) => setOrganisation(event.target.value)}
                            />
                             <TextField
                                 margin="dense"
                                 id="street-address"
                                 label={"Street Address"}
                                 type="text"
                                 fullWidth
                                 value={streetAddress}
                                 onChange={(event: ChangeEvent<HTMLInputElement>) => setStreetAddress(event.target.value)}
                             />
                            <TextField
                                margin="dense"
                                id="city"
                                label={"City"}
                                type="text"
                                fullWidth
                                value={city}
                                onChange={(event: ChangeEvent<HTMLInputElement>) => setCity(event.target.value)}
                            />
                            <TextField
                                margin="dense"
                                id="state"
                                label={"State/Province"}
                                type="text"
                                fullWidth
                                value={state}
                                onChange={(event: ChangeEvent<HTMLInputElement>) => setState(event.target.value)}
                            />
                            <TextField
                                margin="dense"
                                id="postal-code"
                                label={"Zip/Postal Code"}
                                type="text"
                                fullWidth
                                value={postalCode}
                                onChange={(event: ChangeEvent<HTMLInputElement>) => setPostalCode(event.target.value)}
                            />
                            <FormControl fullWidth  margin="dense">
                                <InputLabel id="select-label">Region</InputLabel>
                                <Select
                                    labelId="select-label"
                                    id="simple-select"
                                    value={region}
                                    label="Region"
                                    onChange={(event: SelectChangeEvent) => setRegion(event.target.value as string)}
                                >
                                    <MenuItem value={"EU"}>EU</MenuItem>
                                    <MenuItem value={"CZ"}>CZ</MenuItem>
                                    <MenuItem value={"Other"}>Other</MenuItem>
                                </Select>
                            </FormControl>
                            <TextField
                                margin="dense"
                                id="email-address"
                                label={"Email address"}
                                type="email"
                                fullWidth
                                value={email}
                                onChange={(event: ChangeEvent<HTMLInputElement>) => setEmail(event.target.value)}
                            />
                            {loading 
                                ? <CircularProgress />
                                : <Button
                                    type="submit"
                                    fullWidth
                                    variant="contained"
                                    sx={{ mt: 3, mb: 2 }}
                                    disabled={!isSubmitEnabled()}
                                    >
                                        Confirm
                                    </Button>
                               
                             
                            }
                            
                        </Box>
                    }
                </Box>
            </Container>
    );
}